import { type ErrorEvent, type User, init, setUser, tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { sharedSentryConfig } from "@vyne-shared/utils/sentryConfig";
import { useEffect } from "react";
import configuration from "utils/config";
import type { PatientLoginResponse } from "api/VynePayments/responses";
import type { portalRouter } from "components/Navigation/portalRouter";

/** Initialize Sentry, and enable Sentry in non-development environments */
export const initSentry = (router?: typeof portalRouter) => {
    const sharedConfig = sharedSentryConfig(configuration, tanstackRouterBrowserTracingIntegration(router));

    return init({
        ...sharedConfig,
        dsn: "https://0964eb396dee4e2d884665bfc0df6638@o119772.ingest.sentry.io/6781432", // payments-patientportal
        ignoreErrors: [...sharedConfig.ignoreErrors, "Token Missing"],
        beforeSend(event, hint) {
            // Allow shared beforeSend to process first
            const originalEvent = sharedConfig.beforeSend(event, hint);

            // Downgrade preload CSS errors to warnings, group together
            if (isPreloadCssError(originalEvent)) {
                originalEvent.level = "warning";
                originalEvent.fingerprint = ["preload-css-error"];
            }

            // Downgrade blocked font errors to warnings, group together
            if (isBlockedFontError(originalEvent)) {
                originalEvent.level = "warning";
                originalEvent.fingerprint = ["blocked-font-error"];
            }

            return originalEvent;
        },
    });
};

/** Set sentry user (only re-sets if actually changed) */
export function useSentryUser(result?: PatientLoginResponse) {
    const { id, customerId, customerName, trellisGlobalCustomerId } = buildSentryUser(result);

    useEffect(() => {
        if (id) setUser({ id, customerId, customerName, trellisGlobalCustomerId });
    }, [id, customerId, customerName, trellisGlobalCustomerId]);
}

/**
 * Build a Sentry user object from a login response.
 * @param result The result of logging in.
 * @returns The Sentry user object.
 */
const buildSentryUser = (result?: PatientLoginResponse) =>
    ({
        id: result?.patientId.toString(),
        customerId: result?.customerId,
        customerName: result?.customerName,
        trellisGlobalCustomerId: result?.trellisGlobalCustomerId,
    }) satisfies User;

function getEventMessage(event: ErrorEvent) {
    return event.exception?.values?.[0]?.value ?? event.message ?? "";
}

function isPreloadCssError(event: ErrorEvent) {
    const preloadCssErrorStr = "Unable to preload CSS for ";

    // if message has "Unable to preload CSS for" then it's a preload CSS error
    return getEventMessage(event).includes(preloadCssErrorStr);
}

function isBlockedFontError(event: ErrorEvent) {
    const blockedFontErrorStr = "Blocked 'font' from '";

    // if message has "Blocked 'font' from '" and ".woff2" then it's a blocked font error
    // woff2 is in the URL of the blocked font, not the message
    const message = getEventMessage(event);
    return message.includes(blockedFontErrorStr);
}
